<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { ChoiceField, TextField } from '@haskoning/vue-component-library'
import { useEventsBus } from '@haskoning/vue-component-library'

import { useMooringPointStore } from '@/stores/mooring_point'
import { useAuthorizationStore } from '@/stores/authorization'

import { MOORING_POINT_LABEL } from '@/constants/shipConstants'

const { emit } = useEventsBus()

const store = useMooringPointStore()
const authorizationStore = useAuthorizationStore()

const { selectedItems, fields } = storeToRefs(store)

const props = defineProps({
  item: { type: Object, required: true },
  lastRow: { type: Boolean, default: false }
})

const canEdit = computed(() =>
  authorizationStore.hasServicePermission({
    authSource: 'ProjectUser',
    allowed: 'change_mooringpoint'
  })
)

const getLineAngleStyle = (value) => {
  return value
    ? {
        'border-bottom-width': '2px',
        'border-bottom-style': 'solid',
        'border-bottom-color': value ? value : 'rgba(0, 0, 0, 0.1)'
      }
    : {}
}

const lastRowClass = computed(() => {
  return props.lastRow ? 'border-bottom-grey' : ''
})

const isSelected = computed({
  get: () => {
    return selectedItems.value?.includes(props.item) || false
  },
  set: (value) => {
    store.updateSelectedItems(value, [props.item])
  }
})
</script>

<template>
  <tr
    :class="{
      'selected-item': isSelected
    }"
  >
    <td :class="[lastRowClass, 'text-center px-1']">
      <v-checkbox
        color="primary"
        v-model="isSelected"
        density="compact"
        hide-details
        @click.stop=""
      ></v-checkbox>
    </td>
    <td style="width: 1%" :class="[lastRowClass]">
      <v-chip
        v-ripple
        class="clickable"
        label
        size="x-small"
        :color="item['color']"
        @click.stop="emit('chartEvent', { id: item['id'], label: MOORING_POINT_LABEL })"
      >
        {{ item['name'] }}
      </v-chip>
    </td>
    <td :class="[lastRowClass]">
      <ChoiceField
        :disabled="!canEdit"
        :field="fields['type']"
        item-key="type"
        :model-value="item"
        density="compact"
        variant="plain"
        hide-details
        no-label
        no-padding
        center
        @update="store.update(item)"
      ></ChoiceField>
    </td>
    <td :class="[lastRowClass, 'border-left-grey']">
      <TextField
        :disabled="!canEdit"
        :field="fields['x']"
        item-key="x"
        :model-value="item"
        :show-fraction-digits="true"
        class="text-field"
        density="compact"
        variant="plain"
        hide-details
        no-label
        :step="0.5"
        @update="store.update(item)"
      ></TextField>
    </td>
    <td :class="[lastRowClass]">
      <TextField
        :disabled="!canEdit"
        :field="fields['y']"
        item-key="y"
        :model-value="item"
        :show-fraction-digits="true"
        class="text-field"
        density="compact"
        variant="plain"
        hide-details
        no-label
        :step="0.5"
        no-update-on-error
        @update="store.update(item)"
      ></TextField>
    </td>
    <td :class="[lastRowClass, 'border-right-grey']">
      <TextField
        :disabled="!canEdit"
        :field="fields['z']"
        item-key="z"
        :model-value="item"
        :show-fraction-digits="true"
        class="text-field"
        density="compact"
        variant="plain"
        hide-details
        no-label
        :step="0.5"
        @update="store.update(item)"
      ></TextField>
    </td>
    <td
      :class="[lastRowClass, 'text-center']"
      :style="getLineAngleStyle(item.max_horizontal_color)"
    >
      <div>
        {{ item['max_horizontal_angle']?.toFixed(2) }}
      </div>
    </td>
    <td
      :class="[lastRowClass, 'text-center']"
      :style="getLineAngleStyle(item['max_vertical_color'])"
    >
      <div>
        {{ item['max_vertical_angle']?.toFixed(2) }}
      </div>
    </td>
  </tr>
</template>

<style scoped>
.text-field :deep(.v-field__input) {
  text-align: center;
  padding: 0;
}
</style>
